import { module as Module } from "modujs"
import { gsap, SplitText } from "gsap/all"
import { clamp } from '../utils/maths'
import { EAGER_FONTS } from "../app"
import { whenReady } from "../utils/fonts"
gsap.registerPlugin(SplitText)

const LINE_TAG = 'span'
const CLASS = {
    LINE: 'c-fadein-paragraph_line',
    LINE_INNER: 'c-fadein-paragraph_line_inner'
}

export default class extends Module {
    constructor(m) {
        super(m)

        // Binding
        this.onResizeBind = this.onResize.bind(this)

        // UI
        this.$el    = this.el;
        this.$text  = this.$('text')[0]
        this.$lines = []

        // Data
        this.splits         = []
        this.lineCount      = 0
        this.oldActiveLine  = -1
        this.activeLine     = -1
        this.windowWidth    = 0

        // Progress
        this.progress = 0
    }

    ///////////////
    // Lifecyle
    ///////////////
    init() {
        this.windowWidth = window.innerWidth

        // Events
        this.bindEvents()
        whenReady(EAGER_FONTS).then((fonts) => this.onFontsLoaded(fonts))

        // Get all p tags inside text
        this.$paragraphs = this.$text.querySelectorAll('p')
    }

    destroy() {
        super.destroy()
        this.unbindEvents()
    }

    ///////////////
    // Events
    ///////////////
    bindEvents() {
        window.addEventListener('resizeEnd', this.onResizeBind)
    }

    unbindEvents() {
        window.removeEventListener('resizeEnd', this.onResizeBind)
    }

    ///////////////
    // Callbacks
    ///////////////
    onResize() {
        // If width has not changed, don't redo the split
        if (window.innerWidth == this.windowWidth || this.splits.length == 0) return

        // Reset text as default
        this.revertSplits()

        // Wait a frame to get split reverted
        requestAnimationFrame(async () => {

            // Split again
            await this.initSplits()

            // Wait another frame, so the split is done
            // and a 'onScrollProgress' has been called
            requestAnimationFrame(() => {

                // Add active class to lines
                let index = 0
                while (index < this.activeLine) {
                    this.$lines[index].classList.add('is-active')
                    index++
                }

            })
        })
    }

    onFontsLoaded(fonts) {
        this.initSplits()
    }

    onScrollProgress(progress = 0) {
        this.progress = progress
        const mappedProgress = this.progress * this.lineCount
        const flooredProgress = clamp(
            0,
            this.lineCount - 1,
            Math.floor(mappedProgress)
        )

        if (flooredProgress !== this.activeLine) {
            this.activeLine = flooredProgress
            this.computeActiveLine()
        }
    }

    ///////////////
    // Methods
    ///////////////
    initSplits() {
        return new Promise(resolve => {

            let globalLineIndex = 0
            let paragraphIndex = 0

            while (paragraphIndex < this.$paragraphs.length) {
                const $paragraph = this.$paragraphs[paragraphIndex]

                // Split text
                this.splits[paragraphIndex] = new SplitText($paragraph, {
                    type: 'lines',
                    linesClass: CLASS.LINE,
                    reduceWhiteSpace: false,
                    tag: LINE_TAG
                })

                // Add nesting level to each line for reveal animation purpose
                let lineIndex = 0
                while (lineIndex < this.splits[paragraphIndex].lines.length) {
                    // Get current line DOM
                    const currentLineDom = this.splits[paragraphIndex].lines[lineIndex].innerHTML

                    // Wrap into another span
                    const newLineDom = `<${LINE_TAG} class="${CLASS.LINE_INNER}" style="--line-index:${globalLineIndex}">${currentLineDom}</span>`

                    // Replace DOM
                    this.splits[paragraphIndex].lines[lineIndex].innerHTML = newLineDom

                    globalLineIndex++
                    lineIndex++
                }

                // Add blank line
                if (paragraphIndex > 0) {
                    const emptyLine = document.createElement(LINE_TAG)
                    emptyLine.classList.add('c-fadein-paragraph_line')
                    emptyLine.classList.add('-empty')
                    $paragraph.prepend(emptyLine)

                    // Increment line count
                    this.lineCount++

                    // Add empty line to global array
                    this.$lines.push(emptyLine)
                }

                // Get new lines
                const newLines = this.splits[paragraphIndex].lines

                // Add lines to line count
                this.lineCount += newLines.length

                // Add new lines to global array
                this.$lines.push(...newLines)

                paragraphIndex++
            }

            // Update scroll after addings the empty lines to get progress
            requestAnimationFrame(() => {
                this.onScrollProgress(this.progress)
                resolve('resolved')
            })
        })
    }

    revertSplits() {
        // Revert each split text
        let index = 0
        while (index < this.$paragraphs.length) {
            this.splits[index].revert()
            index++
        }

        // Reset data to default
        this.$lines = []
        this.lineCount = 0
        this.oldActiveLine = -1
        this.activeLine = -1
    }

    computeActiveLine() {
        if (this.oldActiveLine < this.activeLine) {
            this.$lines[this.activeLine].classList.add('is-active')

            // Just to make sure the previous line is also active
            // in case you're scrolling very fast
            this.$lines[this.activeLine - 1]?.classList.add('is-active')
        } else {
            this.$lines[this.activeLine + 1].classList.remove('is-active')
            this.$lines[this.activeLine + 2]?.classList.remove('is-active')
        }

        this.oldActiveLine = this.activeLine
    }
}
